import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { IFieldValue, IRegistrationField, IRegistrationFormOptions } from '../../registration-form.service';
import { DragulaService } from 'ng2-dragula';
import { Subscription } from 'rxjs/Subscription';

export type PendingOp =
  | { kind: 'create'; tempId: number; dto: any; questionIds: number[] }
  | { kind: 'update'; valueId: number; dto: any; questionIds: number[]; origQuestionIds: number[] }
  | { kind: 'delete'; valueId: number }
  | { kind: 'reorder'; order: number[] };

const VALUES_DRAG_BAG = 'reg-field-values-bag';

@Component({
  selector: 'edit-registration-field-values',
  templateUrl: './edit-registration-field-values.component.html',
  styleUrls: ['./edit-registration-field-values.component.scss'],
})
export class EditRegistrationFieldValuesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() field: IRegistrationField;
  @Input() isMultivalue = false;
  @Input() options: IRegistrationFormOptions = {
    inUseOnly: false,
    displayKind: false,
    allowQuotas: false,
    allowCompanions: false,
    allowConditions: false,
    allowWaitingList: false,
  };
  @Input() allFields: IRegistrationField[] = [];
  @Output() pendingOpsChange = new EventEmitter<PendingOp[]>();

  public values: IFieldValue[] = [];
  public pendingOps: PendingOp[] = [];

  // Edit/Create value modal
  public showModal = false;
  public editingValue: IFieldValue | null = null;
  public valueLabel = '';
  public valueQuota: number | null = null;
  public valueAllowWaiting = false;
  public selectedQuestionIds: number[] = [];

  // Delete confirm modal
  public showDeleteModal = false;
  public deletingValue: IFieldValue | null = null;

  // Questions modal
  public showQuestionsModal = false;
  public editingQuestionsValue: IFieldValue | null = null;
  public editingQuestionIds: number[] = [];

  private tempIdCounter = 0;
  private dragulaSubscription: Subscription;
  private dragulaClonedSubscription: Subscription;

  constructor(
    private dragulaService: DragulaService,
  ) {}

  ngOnInit() {
    this.values = (this.field && this.field.values) ? [...this.field.values] : [];

    this.dragulaSubscription = this.dragulaService.drop.subscribe((args: any[]) => {
      const [bagName] = args;
      if (bagName !== VALUES_DRAG_BAG) return;

      const order = this.values.map(v => v.option_value_id);
      this.pendingOps = this.pendingOps.filter(op => op.kind !== 'reorder');
      this.pendingOps.push({ kind: 'reorder', order });
      this.pendingOpsChange.emit([...this.pendingOps]);
    });

    this.dragulaClonedSubscription = this.dragulaService.cloned.subscribe((args: any[]) => {
      const [bagName, clone, original, type] = args;
      if (bagName !== VALUES_DRAG_BAG || type !== 'mirror') return;
      const originalCells = original.children;
      const cloneCells = clone.children;
      for (let i = 0; i < originalCells.length; i++) {
        cloneCells[i].style.width = `${originalCells[i].offsetWidth}px`;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.field && this.field) {
      this.values = this.field.values ? [...this.field.values] : [];
      this.pendingOps = [];
    }
  }

  ngOnDestroy() {
    if (this.dragulaSubscription) {
      this.dragulaSubscription.unsubscribe();
    }
    if (this.dragulaClonedSubscription) {
      this.dragulaClonedSubscription.unsubscribe();
    }
  }

  get isDefault(): boolean {
    return !!(this.field && this.field.is_default);
  }

  get canReorder(): boolean {
    return !this.isDefault;
  }

  get availableQuestionFields(): IRegistrationField[] {
    if (!this.allFields.length || !this.field) return [];
    return this.allFields.filter(f =>
      !f.required &&
      f.option_id !== this.field.option_id &&
      (!this.options.inUseOnly || !f.is_default || !!f.is_in_use)
    );
  }

  // ── Edit/Create value modal ───────────────────────────────────────────────

  openCreate() {
    this.editingValue = null;
    this.valueLabel = '';
    this.valueQuota = null;
    this.valueAllowWaiting = false;
    this.selectedQuestionIds = [];
    this.showModal = true;
  }

  openEdit(value: IFieldValue) {
    this.editingValue = value;
    this.valueLabel = value.label || '';
    this.valueQuota = value.quota != null ? value.quota : null;
    this.valueAllowWaiting = !!value.allow_waiting;
    this.selectedQuestionIds = value.questions_to_display ? [...value.questions_to_display] : [];
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
    this.editingValue = null;
  }

  hasConditions(value: IFieldValue): boolean {
    return !!(value.questions_to_display && value.questions_to_display.length > 0);
  }

  save() {
    const dto: any = { label: this.valueLabel };
    if (!this.isDefault) {
      dto.allow_waiting = this.valueAllowWaiting;
      dto.quota = this.valueQuota || null;
    }

    if (this.editingValue) {
      const origQuestionIds = this.editingValue.questions_to_display
        ? [...this.editingValue.questions_to_display]
        : [];

      this.pendingOps = this.pendingOps.filter(op =>
        !(op.kind === 'update' && op.valueId === this.editingValue!.option_value_id)
      );
      this.pendingOps.push({
        kind: 'update',
        valueId: this.editingValue.option_value_id,
        dto,
        questionIds: [...this.selectedQuestionIds],
        origQuestionIds,
      });

      const idx = this.values.findIndex(v => v.option_value_id === this.editingValue!.option_value_id);
      if (idx >= 0) {
        this.values[idx] = {
          ...this.values[idx],
          label: this.valueLabel,
          quota: dto.quota,
          allow_waiting: dto.allow_waiting,
          questions_to_display: [...this.selectedQuestionIds],
        };
      }
    } else {
      const tempId = --this.tempIdCounter;
      this.pendingOps.push({
        kind: 'create',
        tempId,
        dto,
        questionIds: [...this.selectedQuestionIds],
      });
      this.values.push({
        option_value_id: tempId,
        label: this.valueLabel,
        quota: dto.quota,
        allow_waiting: dto.allow_waiting,
        questions_to_display: [...this.selectedQuestionIds],
      });
    }

    this.pendingOpsChange.emit([...this.pendingOps]);
    this.closeModal();
  }

  // ── Questions modal ───────────────────────────────────────────────────────

  openQuestionsModal(value: IFieldValue) {
    this.editingQuestionsValue = value;
    this.editingQuestionIds = value.questions_to_display ? [...value.questions_to_display] : [];
    this.showQuestionsModal = true;
  }

  closeQuestionsModal() {
    this.showQuestionsModal = false;
    this.editingQuestionsValue = null;
  }

  isQuestionSelectedForEditing(optionId: number): boolean {
    return this.editingQuestionIds.indexOf(optionId) >= 0;
  }

  toggleQuestionForEditing(optionId: number) {
    const idx = this.editingQuestionIds.indexOf(optionId);
    if (idx >= 0) {
      this.editingQuestionIds.splice(idx, 1);
    } else {
      this.editingQuestionIds.push(optionId);
    }
  }

  saveQuestions() {
    if (!this.editingQuestionsValue) return;
    const value = this.editingQuestionsValue;
    const origQuestionIds = value.questions_to_display ? [...value.questions_to_display] : [];

    const idx = this.values.findIndex(v => v.option_value_id === value.option_value_id);
    if (idx >= 0) {
      this.values[idx] = { ...this.values[idx], questions_to_display: [...this.editingQuestionIds] };
    }

    this.pendingOps = this.pendingOps.filter(op =>
      !(op.kind === 'update' && op.valueId === value.option_value_id)
    );
    const priorUpdate = this.pendingOps.find(op => op.kind === 'update' && (op as any).valueId === value.option_value_id);
    const dto = priorUpdate ? (priorUpdate as any).dto : { label: value.label };
    this.pendingOps.push({
      kind: 'update',
      valueId: value.option_value_id,
      dto,
      questionIds: [...this.editingQuestionIds],
      origQuestionIds,
    });

    this.pendingOpsChange.emit([...this.pendingOps]);
    this.closeQuestionsModal();
  }

  // ── Delete modal ──────────────────────────────────────────────────────────

  confirmRemove(value: IFieldValue) {
    this.deletingValue = value;
    this.showDeleteModal = true;
  }

  cancelRemove() {
    this.showDeleteModal = false;
    this.deletingValue = null;
  }

  removeValue() {
    if (!this.deletingValue) return;
    const valueId = this.deletingValue.option_value_id;

    if (valueId < 0) {
      this.pendingOps = this.pendingOps.filter(op => op.kind !== 'create');
    } else {
      this.pendingOps = this.pendingOps.filter(op =>
        !(op.kind === 'update' && op.valueId === valueId)
      );
      this.pendingOps.push({ kind: 'delete', valueId });
    }

    this.values = this.values.filter(v => v.option_value_id !== valueId);
    this.pendingOpsChange.emit([...this.pendingOps]);
    this.cancelRemove();
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/sessions/registration-form/edit-registration-field-values/edit-registration-field-values.component.ts